import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	FormHelperText,
	Grid,
	Button as MuiButton,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from '@material-ui/core';
import { Button, Loading } from 'src/components';
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons';
import { Fragment, type FunctionComponent, useEffect, useState } from 'react';
import { type ISavedExam, type ISection, type IVideo } from 'src/models';
import _ from 'lodash';
import useStyles from './Course-styles';

import { secureLocalStorage } from 'src/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import courseActions from 'src/store/Courses/actions';
import sectionActions from 'src/store/Sections/actions';
import videoActions from 'src/store/Videos/actions';

const Course: FunctionComponent = () => {
	const { courseId }: { courseId: string } = useParams();
	const history = useHistory();
	// NOTE: Ver todas las notas de ./src/modules/private/Admin.tsx
	const dispatch = useDispatch();
	const classes = useStyles();

	const user = useSelector((state) => state.auth.data);
	const course = useSelector((state) => state.courses.course);
	const isLoading = useSelector((state) => state.courses.isLoading);
	const sections = useSelector((state) => state.sections.sections);

	const getCourse = (id: number, userId?: string) => dispatch(courseActions.getOne(id, userId));
	const getSections = (courseId: number) => dispatch(sectionActions.getByUser(courseId));
	const markSeen = (videoId: number, sectionId: number) =>
		dispatch(videoActions.markSeen(videoId, sectionId));
	const courseStarted = (userId: string, courseId: number) =>
		dispatch(courseActions.courseStarted(userId, courseId));
	const courseFinished = (userId: string, courseId: number) =>
		dispatch(courseActions.courseFinished(userId, courseId));
	const sectionTrack = (sectionId: number, userId: string, type: string) =>
		dispatch(sectionActions.track(sectionId, userId, type));
	// Const cleanCourse = () => dispatch(courseActions.setOne({}));
	// const cleanSections = () => dispatch(sectionActions.setAll([]));

	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [currentSectionId, setCurrentSectionId] = useState(0);
	const [currentVideo, setCurrentVideo] = useState<IVideo>();
	const [savedExam, setSavedExam] = useState<ISavedExam>({});

	useEffect(() => {
		if (courseId && user.id) {
			getCourse(Number(courseId), user.id);
			getSections(Number(courseId));
			hasSavedExam();
		}

		return () => {
			// CleanCourse();
			// cleanSections();
		};
	}, [courseId, user.id]);

	useEffect(() => {
		const video = document.getElementById(`video-${currentVideo?.id}`);
		video?.addEventListener('ended', handleMarkSeen);

		return () => {
			video?.removeEventListener('ended', handleMarkSeen);
		};
	}, [currentVideo?.id]);

	useEffect(() => {
		if (currentSectionId) {
			// NOTE:
			// Find devuelve undefined en el caso de que no encuentre valores, en ese caso se debe
			// de cuestionar si el atributo existe para no atrapar un error.
			const section = sections.find((section: ISection) => section.id == currentSectionId);

			if (!section?.startedAt) {
				sectionTrack(currentSectionId, user.id, 'started');
			}
		}
	}, [currentSectionId]);

	// NOTE:
	// Recomendamos usar useCallback para cachear las funciones que dependan de dispatch
	// https://react.dev/reference/react/useCallback
	function handleMarkSeen() {
		if (!currentVideo?.seen) markSeen(currentVideo?.id || 0, currentSectionId);
	}

	function handleSelectVideo(video: IVideo, sectionId: number) {
		setCurrentVideo(video);
		setCurrentSectionId(sectionId);
		if (!course.started) courseStarted(user.id, Number(courseId));
	}

	function canTakeExam() {
		let value = false;
		if (sections.length === 0) return value;

		if (currentSectionId) {
			const section = sections.find((section: ISection) => section.id == currentSectionId);
			const _value = section?.videos?.every((video: IVideo) => video.seen) || false;

			if (_value && !section.finishedAt) {
				sectionTrack(currentSectionId, user.id, 'finished');
			}
		}

		value = sections.every((section: ISection) => section.videos?.every((video: IVideo) => video.seen));

		if (typeof course.finished !== 'undefined') {
			if (Boolean(value) && !course.finished) {
				courseFinished(user.id, Number(courseId));
			}
		}

		return value;
	}

	function canTakePartialExam(sectionId: number) {
		if (sections.length === 0) return;
		// NOTE:
		// ./src/modules/Private/Courses/Course.tsx[67:16]
		const section = sections.find((section: ISection) => section.id == sectionId);
		const value = section?.videos?.every((video: IVideo) => video.seen) || false;

		if (value && !section.finishedAt) {
			sectionTrack(sectionId, user.id, 'finished');
		}

		return value;
	}

	function getTakeExamenButton(examType: 'global' | 'partial', sectionId?: number) {
		// NOTE:
		// ./src/modules/Private/Courses/Course.tsx[67:16]
		const section = sections.find((section: ISection) => section.id == sectionId);

		if (examType === 'global' ? canTakeExam() : canTakePartialExam(sectionId || 0))
			return (
				<div className={classes.buttonContainer}>
					<Button
						actionType={'save'}
						disabled={(
							examType === 'global'
								? (course.attempt || 0) >= (course.maxAttempts || 0) || course.passed
								: (section?.attempt || 0) >= (course.maxAttempts || 0) || section?.passed
						)}
						fullWidth
						onClick={() => {
							history.push(
								`/app/courses/exam/${courseId}${examType === 'partial' ? `/${sectionId}` : ''
								}`,
							);
						}}
					>
						Tomar examen
					</Button>

					{examType == 'global' ? (
						<>
							{(course.attempt || 0) >= (course.maxAttempts || 0) && !course.passed && (
								<FormHelperText className={classes.errorText}>
									{' '}
									Se terminaron los intentos{' '}
								</FormHelperText>
							)}

							{course.passed && (
								<FormHelperText className={classes.successText}>
									{' '}
									Este examen ya se completó{' '}
								</FormHelperText>
							)}
						</>
					) : (
						<>
							{(section?.attempt || 0) >= (course.maxAttempts || 0) && !section?.passed && (
								<FormHelperText className={classes.errorText}>
									{' '}
									Se terminaron los intentos{' '}
								</FormHelperText>
							)}

							{section?.passed && (
								<FormHelperText className={classes.successText}>
									{' '}
									Este examen ya se completó{' '}
								</FormHelperText>
							)}
						</>
					)}
				</div>
			);
	}

	function hasSavedExam() {
		const savedExams: ISavedExam[] = secureLocalStorage.get('ues') || [];
		const savedExam = savedExams.find(
			(savedExam: ISavedExam) =>
				savedExam.userId == user.id && savedExam.exam?.id == Number(courseId),
		);
		setSavedExam(savedExam ?? {});

		if (!_.isEmpty(savedExam)) {
			setConfirmationOpen(true);
		}
	}

	function deleteSavedExam() {
		const savedExams: ISavedExam[] = secureLocalStorage.get('ues') || [];
		const index = savedExams.findIndex(
			(savedExam: ISavedExam) =>
				savedExam.userId == user.id && savedExam.exam?.id == Number(courseId),
		);

		if (index > -1) {
			savedExams.splice(index, 1);
			secureLocalStorage.set('ues', savedExams);
		}
	}

	function isSectionEnabled(index: number): boolean {
		if (index === 0) return true;
		const _sections = _.sortBy(sections, 'sortOrder');

		return _sections[index - 1]?.videos?.every((video: IVideo) => video.seen) || false;
	}

	return (
		<>
			<Dialog
				fullWidth
				maxWidth={'sm'}
				onClose={() => {
					setConfirmationOpen(false);
				}}
				open={confirmationOpen}
			>
				<DialogTitle disableTypography>
					<Typography variant={'h5'}> Examen pendiente </Typography>
				</DialogTitle>

				<DialogContent>
					<DialogContentText>
						Tienes un examen por terminar en este curso. <br />
						Puedes terminarlo después pero recuerda que si inicias otro examen se perderá este
						avance guardado.
					</DialogContentText>
				</DialogContent>

				<DialogActions className={classes.dialogActions}>
					<div>
						<MuiButton
							onClick={() => {
								deleteSavedExam();
								setConfirmationOpen(false);
							}}
							style={{ textTransform: 'capitalize' }}
						>
							Eliminar avance
						</MuiButton>
					</div>

					<div>
						<Button
							onClick={() => {
								setConfirmationOpen(false);
							}}
							style={{ textTransform: 'capitalize' }}
							variant={'outlined'}
						>
							Terminar después
						</Button>

						<Button
							color={'secondary'}
							disableElevation
							onClick={() => {
								setConfirmationOpen(false);
								if (savedExam.exam?.exam === 'global') {
									history.push(`/app/courses/exam/${savedExam.exam?.id}`);
								} else {
									history.push(
										`/app/courses/exam/${savedExam.exam?.id}/${!!savedExam.exam?.sections && savedExam.exam?.sections?.length > 0
											? savedExam.exam?.sections[0].id
											: 0
										}`,
									);
								}
							}}
							style={{
								marginLeft: 16,
								textTransform: 'capitalize',
							}}
							variant={'contained'}
						>
							Continuar examen
						</Button>
					</div>
				</DialogActions>
			</Dialog>

			<Loading isLoading={isLoading}>
				<Grid container>
					<h1> {course.name} </h1>

					<Divider className={classes.divider} />

					<Grid container spacing={2}>
						<Grid className={classes.sectionContainer} item md={3} xs={12}>
							{_.sortBy(sections, 'sortOrder').map((section: ISection, index: number) => {
								if ((section.videos?.length || 0) > 0)
									return (
										<Fragment key={section.id}>
											<Accordion disabled={!isSectionEnabled(index)}>
												<AccordionSummary expandIcon={<ExpandMoreIcon />}>
													<Typography> {section.name} </Typography>
												</AccordionSummary>

												<AccordionDetails>
													<Stepper
														className={classes.stepsContainer}
														orientation={'vertical'}
													>
														{_.sortBy(section.videos, 'sortOrder')?.map(
															(video: IVideo) => (
																<Step
																	color={'secondary'}
																	completed={video.seen}
																	key={video.id}
																	onClick={() => {
																		handleSelectVideo(
																			video,
																			section.id || 0,
																		);
																	}}
																>
																	<StepLabel
																		className={classes.activeSection}
																	>
																		{video.name}
																	</StepLabel>
																</Step>
															),
														)}
													</Stepper>
												</AccordionDetails>
											</Accordion>

											{course.exam === 'partial' &&
												getTakeExamenButton('partial', section.id)}
										</Fragment>
									);
							})}

							{course.exam === 'global' && getTakeExamenButton('global')}
						</Grid>

						{currentVideo?.id && (
							<Grid className={classes.videoContainer} container item md={9} xs={12}>
								<Grid className={classes.video} item xs={12}>
									<video
										controls
										controlsList={'nodownload'}
										id={`video-${currentVideo?.id}`}
										key={currentVideo?.id}
										style={{ maxWidth: '100%' }}
									>
										<source src={currentVideo?.url} type={'video/mp4'} />
									</video>
								</Grid>

								<Divider />

								<Grid item xs={12}>
									<Typography variant={'h2'}> {currentVideo.name} </Typography>

									<Typography variant={'h5'}> {currentVideo.description} </Typography>
								</Grid>
							</Grid>
						)}
					</Grid>
				</Grid>
			</Loading>
		</>
	);
};

export default Course;
